import { canServerPrint, printerMode, usesWindowsPrinter } from "@/lib/printer-mode";
import { printReceiptFontTest } from "@/lib/printReceipt";
import { receiptHeader } from '@/lib/receipt-header';
import sm3LogoUrl from '@/assets/receipt-logo-sm3.png';
import { useAuth } from "@/context/AuthContext";
import WriteButton from "@/components/WriteButton";
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Bluetooth, Wifi, Save, Loader2, Contrast, Download } from "lucide-react";

export default function PrinterSettings() {
  const { readOnly } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [windowsIp, setWindowsIp] = useState("");
  const [windowsName, setWindowsName] = useState(() => (new URLSearchParams(window.location.search).get('windowsPrinter') || '').slice(0, 256));
  const serverPrinting = canServerPrint(form || {});
  const customWidth = Number(form?.custom_paper_width ?? 80);
  const validCustomWidth = Number.isFinite(customWidth) && customWidth >= 40 && customWidth <= 210;
  const invalidPaper = form?.paper === 'custom' && !validCustomWidth;
  const header = receiptHeader(form || {});
  const headerWidth = Number(form?.receipt_header_width_mm ?? 35);
  const nameSize = Number(form?.receipt_name_size ?? 40);
  const invalidHeader = !Number.isFinite(headerWidth) || headerWidth < 25 || headerWidth > 40 || !Number.isInteger(nameSize) || nameSize < 25 || nameSize > 40 || (form?.receipt_header_mode === 'image' && !header.image);
  const uploadHeader = async file => {
    if (!file || readOnly) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Gambar maksimal 2 MB.'); return; }
    setUploadingHeader(true);
    try {
      const body = new FormData(); body.append('file', file);
      const { data } = await api.post('/settings/receipt-header-image', body);
      setForm(f => ({ ...f, receipt_header_image: data.image, receipt_header_mode: 'image' }));
      toast.success('Gambar siap. Tekan Simpan untuk memakai pada struk toko ini.');
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploadingHeader(false); }
  };
  const useSm3Example = async () => {
    if (readOnly || uploadingHeader) return;
    setUploadingHeader(true);
    try {
      const response = await fetch(sm3LogoUrl);
      if (!response.ok) throw new Error('Contoh logo belum dapat dimuat');
      await uploadHeader(new File([await response.blob()], 'LOGO SM 3.png', { type: 'image/png' }));
    } catch (e) { toast.error(e.message); }
    finally { setUploadingHeader(false); }
  };
  const downloadWindowsSetup = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/print/windows-setup", { params: { printer_ip: windowsIp.trim(), printer_name: windowsName.trim() }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a"); link.href = url; link.download = "POS-Cetak-Langsung.zip";
      document.body.appendChild(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Ekstrak ZIP di komputer kasir, lalu jalankan Pasang-POS.cmd");
    } catch (e) { toast.error("Gagal mengunduh pemasangan. Periksa nama printer/alamat IP dan coba lagi."); }
    finally { setDownloading(false); }
  };

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (invalidPaper) { toast.error('Isi lebar custom antara 40 dan 210 mm.'); return; }
    if (invalidHeader || uploadingHeader) { toast.error('Periksa gambar, lebar 25–40 mm, dan ukuran nama 25–40 px.'); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { ...form, custom_paper_width: validCustomWidth ? customWidth : 80, eth_port: Number(form.eth_port || 9100) });
      setForm(data);
      toast.success("Pengaturan printer disimpan");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const [applying, setApplying] = useState(false);
  const applyDensity = async () => {
    if (!serverPrinting) return;
    setApplying(true);
    try {
      const { data } = await api.put("/settings", { ...form, eth_port: Number(form.eth_port || 9100) });
      setForm(data);
      if (!canServerPrint(data)) {
        toast.info("Kepekatan printer lokal diatur melalui driver/utilitas Epson di komputer kasir.");
        return;
      }
      await api.post("/print/density");
      toast.success("Kepekatan diterapkan ke printer. Coba cetak ulang struk.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setApplying(false);
    }
  };

  if (!form) return (<><PageHeader title="Pengaturan Printer" /><div className="flex-1 flex items-center justify-center text-ui-muted">Memuat…</div></>);

  return (
    <>
      <PageHeader title="Pengaturan Printer" subtitle="Struk thermal · Bluetooth & Ethernet">
        <WriteButton data-testid="save-settings-btn" onClick={save} disabled={saving || uploadingHeader || invalidPaper || invalidHeader} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan
        </WriteButton>
      </PageHeader>

      <div className="flex-1 overflow-y-auto p-5" data-testid="printer-settings">
        <div className="max-w-2xl space-y-5">
          <section data-testid="windows-print-setup" className="rounded-xl border border-brand/30 bg-brand-soft p-4 space-y-3">
            <div className="font-semibold">Cetak langsung khusus POS Windows</div>
            <p className="text-sm text-ui-muted">Untuk Epson USB atau LAN yang terpasang di Windows. Pemasangan membuat shortcut POS dengan printer sendiri; tidak memerlukan izin printer server.</p>
            <div className="space-y-1.5"><Label htmlFor="windows-printer-name">Nama printer di Windows (opsional)</Label>
              <Input disabled={readOnly} id="windows-printer-name" data-testid="windows-printer-name" value={windowsName} onChange={e => setWindowsName(e.target.value)} maxLength={256} placeholder="Nama persis seperti di daftar printer Windows" className="bg-ui-card border-ui-line" />
              <p className="text-xs text-ui-muted">Isi nama printer yang berhasil dipakai di Chrome. Pemasang tidak memilih printer lain jika nama ini tidak ditemukan. Cukup isi nama; IP boleh kosong.</p>
            </div>
            <div className="space-y-1.5"><Label htmlFor="windows-printer-ip">IP printer (opsional)</Label>
              <Input disabled={readOnly} id="windows-printer-ip" data-testid="windows-printer-ip" value={windowsIp} onChange={e => setWindowsIp(e.target.value)} placeholder="192.168.111.100" className="bg-ui-card border-ui-line font-mono" />
              <p className="text-xs text-ui-muted">Gunakan untuk port TCP/IP Windows. Jika nama dan IP sama-sama diisi, keduanya harus cocok.</p>
            </div>
            <Button data-testid="download-windows-print" disabled={readOnly || downloading} onClick={downloadWindowsSetup} className="gap-2">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Unduh POS Cetak Langsung
            </Button>
            <p className="text-xs text-ui-muted">Ekstrak ZIP → jalankan Pasang-POS.cmd → buka shortcut POS Cetak Langsung di desktop. Chrome dan driver Epson harus sudah terpasang.</p>
            {usesWindowsPrinter() && <p data-testid="windows-print-active" className="text-sm font-semibold text-brand-readable">Profil ini mencetak melalui driver Windows. Gunakan shortcut POS Cetak Langsung agar cetak berjalan tanpa konfirmasi browser.</p>}
            <p data-testid="printer-route-status" className="text-xs text-ui-muted">Jalur cetak aktif: {printerMode(form) === 'browser' ? 'komputer/perangkat ini (USB atau LAN melalui driver), bukan server POS.' : printerMode(form) === 'bluetooth' ? 'Bluetooth perangkat ini.' : 'server POS ke tujuan yang disetujui.'}</p>
          </section>
          {/* Store identity */}
          <section className="rounded-xl border border-ui-line bg-ui-soft p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-ui-muted"><Printer className="h-4 w-4 text-brand-readable" /> Header & Footer Struk</div>
            <div className="space-y-2">
              <Label htmlFor="receipt-header-mode">Tampilan nama toko pada struk</Label>
              <select id="receipt-header-mode" data-testid="receipt-header-mode" disabled={readOnly || uploadingHeader} value={form.receipt_header_mode || 'text'} onChange={e => set('receipt_header_mode', e.target.value)} className="w-full rounded-md border border-ui-line bg-ui-card p-2 text-sm">
                <option value="text">Nama teks (dengan logo aplikasi bila ada)</option>
                <option value="image">Gambar pengganti nama toko</option>
                <option value="none">Tanpa nama / gambar toko</option>
              </select>
              <p className="text-xs text-ui-muted">Khusus struk Komputer / Browser dan POS Cetak Langsung. Tidak mengubah logo aplikasi atau toko lain. Bluetooth/Ethernet via Server tetap menggunakan nama teks.</p>
              {form.receipt_header_mode === 'image' && <div className="space-y-3 border border-ui-line rounded-lg p-3">
                <Label htmlFor="receipt-header-upload">Unggah / ganti gambar nama toko</Label>
                <Input id="receipt-header-upload" data-testid="receipt-header-upload" type="file" accept="image/png,image/jpeg,image/webp" disabled={readOnly || uploadingHeader} onChange={e => { const file = e.target.files?.[0]; e.target.value = ''; uploadHeader(file); }} />
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" data-testid="receipt-header-sm3" disabled={readOnly || uploadingHeader} onClick={useSm3Example}>Gunakan contoh LOGO SM 3</Button>
                  {header.image && <Button type="button" variant="outline" data-testid="receipt-header-remove" disabled={readOnly || uploadingHeader} onClick={() => setForm(f => ({ ...f, receipt_header_image: null, receipt_header_mode: 'text' }))}>Hapus gambar</Button>}
                </div>
                <p className="text-xs text-ui-muted">PNG, JPG, WebP maksimal 2 MB. Tepi kosong dirapikan otomatis; gambar disiapkan hitam-putih. Perubahan berlaku setelah Simpan.</p>
                <div className="flex items-center gap-3"><Label htmlFor="receipt-header-width">Lebar gambar (mm)</Label><Input id="receipt-header-width" data-testid="receipt-header-width" type="number" min="25" max="40" step="1" value={form.receipt_header_width_mm ?? 35} disabled={readOnly || uploadingHeader} onChange={e => set('receipt_header_width_mm', e.target.value)} className="w-24" /></div>
                {uploadingHeader ? <p role="status" className="text-sm">Menyiapkan gambar…</p> : header.image && <div className="bg-white rounded-md p-3 overflow-hidden" data-testid="receipt-header-preview"><img src={header.image} alt="Pratinjau gambar nama toko pada struk" style={{ width: `${header.width}mm`, maxWidth: '100%', height: 'auto', margin: 'auto' }} /></div>}
              </div>}
              {(form.receipt_header_mode || 'text') === 'text' && <div className="flex items-center gap-3"><Label htmlFor="receipt-name-size">Ukuran nama teks (px)</Label><Input id="receipt-name-size" data-testid="receipt-name-size" type="number" min="25" max="40" step="1" value={form.receipt_name_size ?? 40} disabled={readOnly} onChange={e => set('receipt_name_size', e.target.value)} className="w-24" /></div>}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-ui-muted">Nama Toko</Label>
              <Input disabled={readOnly} data-testid="set-store-name" value={form.store_name} onChange={(e) => set("store_name", e.target.value)} className="bg-ui-card border-ui-line" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-ui-muted">Alamat</Label>
                <Input disabled={readOnly} data-testid="set-address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="bg-ui-card border-ui-line" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-ui-muted">Telepon</Label>
                <Input disabled={readOnly} data-testid="set-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="bg-ui-card border-ui-line" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-ui-muted">Catatan Kaki (Footer)</Label>
              <Textarea disabled={readOnly} data-testid="set-footer" value={form.footer || ""} onChange={(e) => set("footer", e.target.value)} rows={2} className="bg-ui-card border-ui-line" />
            </div>
          </section>

          {/* Paper & mode */}
          <section className="rounded-xl border border-ui-line bg-ui-soft p-4 space-y-3">
            <div className="text-sm font-semibold text-ui-muted">Perangkat & Kertas</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-ui-muted">Ukuran Kertas</Label>
                <Select disabled={readOnly} value={form.paper} onValueChange={(v) => setForm(f => ({ ...f, paper: v, ...(v === 'custom' ? { mode: 'browser', custom_paper_width: f.custom_paper_width ?? 80 } : {}) }))}>
                  <SelectTrigger data-testid="set-paper" className="bg-ui-card border-ui-line"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ui-card border-ui-line text-ui-text">
                    <SelectItem value="58">58mm</SelectItem>
                    <SelectItem value="80">80mm</SelectItem>
                    <SelectItem value="custom" disabled={printerMode(form) !== 'browser'}>Custom — isi lebar sendiri</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-ui-muted">Metode Utama</Label>
                <Select disabled={readOnly || usesWindowsPrinter()} value={printerMode(form)} onValueChange={(v) => set("mode", v)}>
                  <SelectTrigger data-testid="set-mode" className="bg-ui-card border-ui-line"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ui-card border-ui-line text-ui-text">
                    <SelectItem value="browser">Komputer / Browser (USB & LAN)</SelectItem>
                    <SelectItem value="bluetooth" disabled={form.paper === 'custom'}>Bluetooth</SelectItem>
                    <SelectItem value="ethernet" disabled={!serverPrinting || form.paper === 'custom'}>Ethernet via Server (khusus)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.paper === 'custom' && <div data-testid="custom-paper-settings" className="space-y-1.5">
              <Label htmlFor="custom-paper-width">Lebar kertas custom (mm)</Label>
              <Input id="custom-paper-width" data-testid="custom-paper-width" disabled={readOnly} type="number" min="40" max="210" step="0.1" value={form.custom_paper_width ?? 80} onChange={e => set('custom_paper_width', e.target.value)} aria-invalid={invalidPaper} aria-describedby="custom-paper-help" className="bg-ui-card border-ui-line max-w-48" />
              <p id="custom-paper-help" className="text-xs text-ui-muted">Cukup isi lebar 40–210 mm. Area teks diberi ruang aman agar tidak terpotong. Tidak mengatur panjang atau pemotongan kertas; keduanya mengikuti driver. Pilih lebar yang didukung printer dan samakan pengaturan kertas di driver Windows.</p>
              {invalidPaper && <p role="alert" className="text-xs text-danger">Isi lebar antara 40 dan 210 mm.</p>}
            </div>}
            <div className="flex items-center justify-between pt-1">
              <div>
                <Label className="text-sm text-ui-text">Cetak otomatis setelah bayar</Label>
                <p className="text-[11px] text-ui-muted">Cetak dipanggil setelah pembayaran berhasil. Buka shortcut POS Cetak Langsung agar tanpa dialog; Chrome biasa tetap menampilkan dialog cetak.</p>
              </div>
              <Switch disabled={readOnly} data-testid="set-autoprint" checked={form.auto_print} onCheckedChange={(v) => set("auto_print", v)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="receipt-ink">Kepekatan teks struk Windows / Browser</Label>
              <Select disabled={readOnly} value={form.receipt_ink || 'dark'} onValueChange={v => set('receipt_ink', v)}>
                <SelectTrigger id="receipt-ink" data-testid="set-receipt-ink" className="bg-ui-card border-ui-line"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ui-card border-ui-line text-ui-text">
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="dark">Pekat (disarankan)</SelectItem>
                  <SelectItem value="extra">Lebih pekat</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-ui-muted">Memperkuat garis huruf hitam tanpa mengubah semua rincian menjadi bold. Tidak mengubah suhu printer. Tes dahulu, lalu Simpan.</p>
            </div>
            <Button type="button" variant="outline" data-testid="print-font-test" disabled={invalidPaper || invalidHeader || uploadingHeader} className="gap-2" onClick={() => {
              if (!printReceiptFontTest(form)) toast.error("Izinkan pop-up untuk mencetak contoh font.");
            }}><Printer className="h-4 w-4" /> Tes Format Struk</Button>
            <p className="text-xs text-ui-muted">Tes mengikuti pilihan header di atas: nama teks 25–40 px atau gambar lebar 25–40 mm. Rincian Roboto Condensed 12 px, total 14 px, angka 0 polos. Tanpa membuat transaksi; gunakan skala cetak 100%.</p>
          </section>

          {/* Bluetooth info */}
          <section className="rounded-xl border border-ui-line bg-ui-soft p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-ui-muted"><Bluetooth className="h-4 w-4 text-accent-readable" /> Printer Bluetooth</div>
            <p className="text-xs text-ui-muted">Saat mencetak, tekan tombol <b>Bluetooth</b> di struk lalu pilih printer Anda. Berfungsi di Chrome (Android/desktop) via Web Bluetooth — cocok untuk printer thermal BLE di tablet.</p>
          </section>

          {/* Ethernet */}
          <section className="rounded-xl border border-ui-line bg-ui-soft p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-ui-muted"><Wifi className="h-4 w-4 text-brand-readable" /> Ethernet via Server (khusus)</div>
            <div className="grid grid-cols-[1fr_120px] gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-ui-muted">IP Printer</Label>
                <Input disabled={readOnly} data-testid="set-eth-ip" value={form.eth_ip || ""} onChange={(e) => set("eth_ip", e.target.value)} placeholder="192.168.1.100" className="bg-ui-card border-ui-line font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-ui-muted">Port</Label>
                <Input disabled={readOnly} data-testid="set-eth-port" type="number" value={form.eth_port} onChange={(e) => set("eth_port", e.target.value)} className="bg-ui-card border-ui-line font-mono" />
              </div>
            </div>
            <p className="text-xs text-ui-muted">Bagian ini hanya untuk koneksi dari server POS. Printer LAN di toko seperti TM-T82X tetap memakai Komputer / Browser atau shortcut POS Cetak Langsung. IP dan port di sini tidak memilih printer Windows.</p>
          </section>

          {/* Kepekatan (Epson) */}
          <section className="rounded-xl border border-ui-line bg-ui-soft p-4 space-y-3" data-testid="density-section">
            <div className="flex items-center gap-2 text-sm font-semibold text-ui-muted"><Contrast className="h-4 w-4 text-brand-readable" /> Kepekatan Cetak (Epson TM-T82X)</div>
            <p data-testid="printer-density-help" className="text-xs text-ui-muted">{serverPrinting ? 'Pengaturan ini dikirim langsung dari server ke printer yang disetujui.' : 'Untuk printer USB/LAN melalui Windows, atur kepekatan dan kecepatan melalui driver/utilitas Epson pada komputer kasir. POS tidak dapat menerapkannya melalui dialog cetak browser.'}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-ui-muted">Kepekatan (Density)</Label>
                <Select disabled={readOnly || !serverPrinting} value={String(form.density ?? 6)} onValueChange={(v) => set("density", Number(v))}>
                  <SelectTrigger data-testid="set-density" className="bg-ui-card border-ui-line"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ui-card border-ui-line text-ui-text">
                    <SelectItem value="0">100% (Normal)</SelectItem>
                    <SelectItem value="1">105%</SelectItem>
                    <SelectItem value="2">110%</SelectItem>
                    <SelectItem value="3">115%</SelectItem>
                    <SelectItem value="4">120%</SelectItem>
                    <SelectItem value="5">125%</SelectItem>
                    <SelectItem value="6">130% (Paling Pekat)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-ui-muted">Kecepatan Cetak</Label>
                <Select disabled={readOnly || !serverPrinting} value={String(form.print_speed ?? 2)} onValueChange={(v) => set("print_speed", Number(v))}>
                  <SelectTrigger data-testid="set-speed" className="bg-ui-card border-ui-line"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ui-card border-ui-line text-ui-text">
                    <SelectItem value="1">Level 1 (Paling lambat, paling pekat)</SelectItem>
                    <SelectItem value="2">Level 2</SelectItem>
                    <SelectItem value="3">Level 3</SelectItem>
                    <SelectItem value="5">Level 5</SelectItem>
                    <SelectItem value="10">Level 10 (Paling cepat)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <WriteButton data-testid="apply-density-btn" onClick={applyDensity} disabled={applying || !serverPrinting} variant="outline" className="w-full gap-2 border-brand/50 text-brand-readable hover:bg-brand-soft">
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Contrast className="h-4 w-4" />} Terapkan Kepekatan ke Printer
            </WriteButton>
          </section>
        </div>
      </div>
    </>
  );
}
